import { z } from 'zod'

export const MIN_NEW_PASSWORD_LENGTH = 12

/**
 * Password shape for *entering an existing* password - login, and the
 * `currentPassword` field. Deliberately lenient: accounts created under the
 * old 6-character floor must still be able to sign in and change their
 * password. Use `NewPasswordSchema` for anything that *sets* a password.
 */
export const PasswordSchema = z
	.string({ error: 'Password is required' })
	.min(6, { message: 'Password is too short' })
	// NOTE: bcrypt has a limit of 72 bytes (which should be plenty long)
	// https://github.com/epicweb-dev/epic-stack/issues/918
	.refine((val) => new TextEncoder().encode(val).length <= 72, {
		message: 'Password is too long',
	})

/**
 * Password shape for *setting* a password - signup, reset, create, change.
 * Length only; the HIBP breach check is `checkIsCommonPassword`, already run
 * in each of those actions, so this schema stays synchronous for client-side
 * validation.
 */
export const NewPasswordSchema = PasswordSchema.min(MIN_NEW_PASSWORD_LENGTH, {
	message: `Password must be at least ${MIN_NEW_PASSWORD_LENGTH} characters`,
})

export const NameSchema = z
	.string({ error: 'Name is required' })
	.min(1, { message: 'Name is required' })
	.max(40, { message: 'Name is too long' })
	.trim()

export const EmailSchema = z
	.string({ error: 'Email is required' })
	.email({ message: 'Email is invalid' })
	.min(3, { message: 'Email is too short' })
	.max(100, { message: 'Email is too long' })
	// users can type the email in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordAndConfirmPasswordSchema = z
	.object({ password: NewPasswordSchema, confirmPassword: NewPasswordSchema })
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})
